import { useCitySelectedContext } from '@porterin/feature/src/contexts/city-context/CityContext';
import {
  CityService,
  FareEstimateContent,
} from '@porterin/feature/src/shared/cities/domain/entities/CitiesEntity';
import { google_maps_api_key } from '@porterin/feature/src/shared/config/AppConfig';
import {
  CATEGORY,
  getCategoryFromString,
} from '@porterin/feature/src/shared/core/helpers/CommonVehicleHelper';
import { redirect } from '@porterin/feature/src/shared/core/helpers/SharedHelper';
import {
  MIN_WIDTH_DESKTOP,
  useMediaQuery,
} from '@porterin/feature/src/shared/core/hooks/useMediaQuery';
import { isBkfEnabled } from '@porterin/feature/src/shared/helpers/LocaleHelpers';
import { LeadFlowContentEntity } from '@porterin/feature/src/shared/lead-flow/domain/entities/LeadFlowContentEntity';
import CategorySelectorCmp from '@porterin/feature/src/shared/lead-flow/view/component/CategorySelectorCmp';
import EstimateLayout from '@porterin/feature/src/shared/lead-flow/view/component/EstimateLayout';
import SpotFareEstimateWrapperCmp from '@porterin/feature/src/shared/lead-flow/view/component/SpotFareEstimateWrapperCmp';
import { make as TrucksFormCmp } from '@porterin/feature/src/shared/lead-flow/view/component/TrucksFormCmp.mjs';
import { make as TwoWheelerFormCmp } from '@porterin/feature/src/shared/lead-flow/view/component/TwoWheelerFormCmp.mjs';
import { getVm } from '@porterin/feature/src/shared/lead-flow/view/view-model/LeadFlowVm';
import SpotEstimateFormLoaderCmp from '@porterin/feature/src/shared/spot-lead-flow/view/components/SpotEstimateFormLoaderCmp';
import { make as SpotEstimateFormCmp } from '@porterin/feature/src/shared/spot-lead-flow/view/SpotEstimateFormCmp.mjs';
import { TextSubtext } from '@porterin/feature/src/shared/types/CommonVM';
import GoogleMapsApiLoader from '@porterin/lead-flow/src/components/GoogleMapsApiLoader';
import { useRouter } from 'next/router';
import React, { useState } from 'react';

import styles from '../styles/EstimateLayout.module.scss';

interface FareEstimateHomeModalCmpProps {
  close_modal: () => void;
  category_cards_data: CityService[];
  fare_estimate_form_content: LeadFlowContentEntity;
  modal_content: FareEstimateContent;
}

interface VehicleBranch {
  text_subtext: TextSubtext;
  allow_bkf: boolean;
  DesktopFormCmp: React.ComponentType<Record<string, unknown>>;
  desktop_category_name: string;
  spot_service_category: number;
  wrapper_service_category: number;
}

function FareEstimateHomeModalCmp({
  close_modal,
  category_cards_data,
  fare_estimate_form_content,
  modal_content,
}: Readonly<FareEstimateHomeModalCmpProps>): React.ReactElement {
  const [category, setCategory] = useState<number | undefined>(undefined);
  const onChangeCategory = (): void => setCategory(undefined);
  const { query_params } = useCitySelectedContext();
  const isDesktop = useMediaQuery(MIN_WIDTH_DESKTOP);
  const contentVm = getVm(fare_estimate_form_content);
  const fareEstimateVm = contentVm.fare_estimate_content;
  const spotFareEstimateVm = contentVm.spot_fare_estimate_content;
  const { locale } = useRouter();

  const onCategorySelect = (selected: string): void => {
    const categoryEntity = getCategoryFromString(selected.toLocaleLowerCase());
    const serviceData = category_cards_data.find(
      category_details =>
        category_details.service_type.toLocaleLowerCase() === selected,
    );
    if (categoryEntity !== undefined && serviceData !== undefined) {
      redirect(serviceData.link + query_params);
    } else {
      setCategory(getCategoryFromString(selected.toLocaleLowerCase()));
    }
  };

  const renderVehicleBranch = ({
    text_subtext,
    allow_bkf,
    DesktopFormCmp,
    desktop_category_name,
    spot_service_category,
    wrapper_service_category,
  }: VehicleBranch): React.ReactElement => {
    const spotForm = isDesktop ? (
      <DesktopFormCmp
        change_label={modal_content.change_label}
        onChangeCategory={onChangeCategory}
        close_modal={close_modal}
        fare_estimate_vm={fareEstimateVm}
        arrow_icon={modal_content.arrow_icon}
        category_name={desktop_category_name}
      />
    ) : (
      <SpotEstimateFormCmp
        service_category={spot_service_category}
        spot_fare_estimate_vm={spotFareEstimateVm}
      />
    );

    return (
      <EstimateLayout
        close_modal={close_modal}
        title={text_subtext.text}
        sub_title={text_subtext.sub_text}
        title_mobile={spotFareEstimateVm.mobile_modal_content.title}
        close_icon={modal_content.close_icon}
        className={styles['form-container']}
      >
        {allow_bkf && isBkfEnabled(locale, isDesktop) ? (
          <GoogleMapsApiLoader
            apiKey={google_maps_api_key}
            loader={<SpotEstimateFormLoaderCmp />}
          >
            <SpotFareEstimateWrapperCmp
              change_label={modal_content.change_label}
              onChangeCategory={onChangeCategory}
              category_name={text_subtext.text}
              is_mobile={!isDesktop}
              service_category={wrapper_service_category}
            />
          </GoogleMapsApiLoader>
        ) : (
          spotForm
        )}
      </EstimateLayout>
    );
  };

  let content: React.ReactElement;
  switch (category) {
    case CATEGORY.TWO_WHEELERS:
      content = renderVehicleBranch({
        text_subtext: modal_content.bike_text_subtext,
        allow_bkf: true,
        DesktopFormCmp: TwoWheelerFormCmp,
        desktop_category_name: modal_content.bike_text_subtext.text,
        spot_service_category: CATEGORY.TWO_WHEELERS,
        wrapper_service_category: CATEGORY.TWO_WHEELERS,
      });
      break;
    case CATEGORY.TRUCKS:
      content = renderVehicleBranch({
        text_subtext: modal_content.trucks_text_subtext,
        allow_bkf: true,
        DesktopFormCmp: TrucksFormCmp,
        desktop_category_name: modal_content.trucks_text_subtext.text,
        spot_service_category: CATEGORY.TRUCKS,
        wrapper_service_category: CATEGORY.TRUCKS,
      });
      break;
    case CATEGORY.COMPACT:
      content = renderVehicleBranch({
        text_subtext: modal_content.compact_dubai_text_subtext,
        allow_bkf: true,
        DesktopFormCmp: TrucksFormCmp,
        desktop_category_name: modal_content.compact_dubai_text_subtext.text,
        spot_service_category: CATEGORY.TRUCKS,
        wrapper_service_category: CATEGORY.COMPACT,
      });
      break;
    case CATEGORY.COURIER:
      content = renderVehicleBranch({
        text_subtext: modal_content.courier_text_subtext,
        allow_bkf: false,
        DesktopFormCmp: TwoWheelerFormCmp,
        desktop_category_name: modal_content.bike_text_subtext.text,
        spot_service_category: CATEGORY.TWO_WHEELERS,
        wrapper_service_category: CATEGORY.COURIER,
      });
      break;
    default:
      content = (
        <EstimateLayout
          close_modal={close_modal}
          title={modal_content.modal_text_subtext.text}
          sub_title={modal_content.modal_text_subtext.sub_text}
          title_mobile={modal_content.modal_text_subtext_mobile.text}
          sub_title_mobile={modal_content.modal_text_subtext_mobile.sub_text}
          close_icon={modal_content.close_icon}
        >
          <CategorySelectorCmp
            onCategorySelect={onCategorySelect}
            category_cards_data={category_cards_data}
            arrow_icon={modal_content.arrow_icon}
          />
        </EstimateLayout>
      );
  }

  return <div>{content}</div>;
}

export default FareEstimateHomeModalCmp;
